import { Icon } from '@iconify/react';
import { Column } from '@tanstack/react-table';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { cn } from '@/Domain/utils/cn';

type Props<TData, TValue> = React.HTMLAttributes<HTMLDivElement> & {
  column: Column<TData, TValue>;
  title: string;
};

export const DataTableColumnHeader = <TData, TValue>({
  column,
  title,
  className,
}: Props<TData, TValue>) => {
  const canHideColumn = column.getCanHide();

  if (!column.getCanSort()) {
    return <div className={cn('text-muted-foreground', className)}>{title}</div>;
  }

  return (
    <div className={cn('flex items-center space-x-1', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="gap-0.5">
          <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent">
            <span>{title}</span>
            {column.getIsSorted() === 'desc' ? (
              <Icon icon="mingcute:arrow-down-line" className="h-4 w-4" />
            ) : column.getIsSorted() === 'asc' ? (
              <Icon icon="mingcute:arrow-up-line" className="h-4 w-4" />
            ) : (
              <Icon icon="mingcute:down-small-line" className="h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
            <Icon
              icon="mingcute:arrow-up-line"
              className="mr-2 h-3.5 w-3.5 text-muted-foreground"
            />
            Asc
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
            <Icon
              icon="mingcute:arrow-down-line"
              className="mr-2 h-3.5 w-3.5 text-muted-foreground"
            />
            Desc
          </DropdownMenuItem>
          {canHideColumn ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
                <Icon
                  icon="mingcute:eye-close-line"
                  className="mr-2 h-3.5 w-3.5 text-muted-foreground"
                />
                Hide
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
